<script setup lang="ts">
import { useTranslation } from '@/plugins/i18n/useTranslation';
import BaseChip from './ui/BaseChip.vue';
import BaseIcon from './ui/BaseIcon.vue';
import BaseAvatar from './ui/BaseAvatar.vue';
import type { AuthState } from '@/stores/authStore';
import { useDisplay } from 'vuetify';
import { ref } from 'vue';
import { useAppConfigStore } from '@/stores/appConfigStore';
import { storeToRefs } from 'pinia';
import { type ExternalLink } from '@/stores/configStore';

export type HeaderProps = {
  externalLinks: ExternalLink[];
  documentationPath: string;
  mode: string;
  isProductionEnvironment: boolean;
  authState: AuthState;
  userInitials: string;
  avatar: string;
  currentTheme: 'light' | 'dark';
  showAuthorizedMenu: boolean;
  serviceColor: string;
};

export type HeaderEmits = {
  (event: 'logout'): void;
  (event: 'change-theme', theme: 'light' | 'dark'): void;
};

const { t } = useTranslation();
const display = useDisplay();
const appConfigStore = useAppConfigStore();
const { sidebarConfig, menuCollapsed } = storeToRefs(appConfigStore);

const props = defineProps<HeaderProps>();
const emit = defineEmits<HeaderEmits>();

const showDrawerToggle = ref(display.smAndDown.value);
const externalLinksDropdownOpen = ref(false);
const profileModalOpen = ref(false);

function setTheme(color: 'light' | 'dark'): void {
  profileModalOpen.value = false;
  emit('change-theme', color);
}

async function onLogout() {
  profileModalOpen.value = false;
  emit('logout');
}

function onResize() {
  showDrawerToggle.value = display.smAndDown.value;
  if (display.smAndDown.value) {
    appConfigStore.setSidebarConfig({
      ...sidebarConfig.value,
      visible: false,
      permanent: false
    });
  } else {
    if (menuCollapsed.value === undefined) {
      appConfigStore.setSidebarConfig({
        ...sidebarConfig.value,
        visible: true,
        permanent: true
      });
    }
  }
}

function toggleDrawer() {
  appConfigStore.setSidebarConfig({
    ...sidebarConfig.value,
    visible: !sidebarConfig.value.visible
  });
}
</script>

<template>
  <header
    v-resize="onResize"
    class="w-100"
    :style="{ backgroundColor: props.serviceColor }"
  >
    <div class="d-flex w-100 h-100 align-center text-white px-4">
      <v-app-bar-nav-icon
        v-if="props.showAuthorizedMenu && showDrawerToggle"
        variant="text"
        @click.stop="toggleDrawer"
      />
      <img
        src="~@/assets/images/grb.svg"
        alt="grb"
      >
      <div class="d-flex flex-column text-small px-1 mr-12">
        <span class="header-span">РЕПУБЛИКА СРБИЈА</span>
        <span class="header-span">МИНИСТАРСТВО ФИНАНСИЈА</span>
        <span class="font-weight-bold header-span">УПРАВА ЗА ТРЕЗОР</span>
      </div>
      <RouterLink
        :to="{ name: 'Home' }"
        class="service-name text-h6 font-weight-medium d-flex items-center"
        data-cy="head-line"
      >
        {{ t('serviceShortName') }}
      </RouterLink>
      <BaseChip
        v-if="!isProductionEnvironment"
        color="success-300"
        variant="elevated"
        density="small"
        class="service-type ml-2"
      >
        {{ t(`environments.${mode}`) }}
      </BaseChip>
      <template v-if="props.showAuthorizedMenu && !showDrawerToggle">
        <div
          v-if="props.externalLinks.length"
          class="ml-auto"
        >
          <v-menu v-model="externalLinksDropdownOpen">
            <template #activator="{ props: activatorProps }">
              <a
                v-bind="activatorProps"
                class="nav-link d-flex align-center mt-px"
              >
                {{ t('message.externalLinks') }}
                <BaseIcon
                  class="mt-px"
                  icon="mdi-triangle-small-down"
                />
              </a>
            </template>
            <v-list>
              <template
                v-for="link in props.externalLinks"
                :key="link.link"
              >
                <v-list-item
                  v-if="!!link.name && !!link.link"
                  class="header-dropdown-item"
                  density="compact"
                  @click="externalLinksDropdownOpen = false"
                >
                  <a
                    class="header-dropdown-link"
                    :href="link.link"
                    target="_blank"
                  >
                    {{ link.name }}
                  </a>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </div>
        <a
          :href="documentationPath"
          target="_blank"
          class="nav-link mx-2"
        >
          {{
            t('message.documentation')
          }}
        </a>
        <v-menu
          v-model="profileModalOpen"
        >
          <template #activator="{ props: activatorProps }">
            <a
              class="ml-2 d-flex align-center justify-between nav-link menu-label"
              v-bind="activatorProps"
            >
              <BaseAvatar
                :img-url="props.avatar"
                :text="userInitials"
              />
              <span
                class="ml-2"
                data-cy="header-username"
              >{{
                props.authState.user.login
              }}</span>
              <BaseIcon icon="mdi-triangle-small-down" />
            </a>
          </template>
          <v-list>
            <v-list-item
              class="header-dropdown-item"
              density="compact"
              @click="profileModalOpen = false"
            >
              <RouterLink
                class="header-dropdown-link"
                data-cy="user-profile"
                :to="{ name: 'UserProfile' }"
              >
                {{ t('message.profile') }}
              </RouterLink>
            </v-list-item>
            <v-list-item
              class="header-dropdown-item"
              density="compact"
              @click="profileModalOpen = false"
            >
              <a
                data-cy="logout"
                class="header-dropdown-link"
                @click="onLogout"
              >
                {{ t('message.logout') }}
              </a>
            </v-list-item>
            <hr>
            <v-list-item
              class="header-dropdown-item"
              density="compact"
              @click="setTheme('light')"
            >
              <a class="header-dropdown-link h-100">
                <span class="d-inline-block w-16 mr-1">
                  <BaseIcon
                    v-if="currentTheme === 'light'"
                    :size="16"
                    icon="mdi-check"
                  />
                </span>
                <span class="d-inline-block border box mr-1 mt-1 bg-white" />
                <span class="light-theme" />{{ t('message.themes.lightTheme') }}
              </a>
            </v-list-item>
            <v-list-item
              class="header-dropdown-item"
              density="compact"
              @click="setTheme('dark')"
            >
              <a class="header-dropdown-link h-100">
                <span class="d-inline-block w-16 mr-1">
                  <BaseIcon
                    v-if="currentTheme === 'dark'"
                    :size="16"
                    icon="mdi-check"
                  />
                </span>
                <span class="d-inline-block bg-black box mr-1 mt-1" />
                <span class="dark-theme" />{{ t('message.themes.darkTheme') }}
              </a>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </div>
  </header>
</template>

<style scoped lang="scss">
header {
  height: 70px;

  img {
    width: 30px;
    margin-bottom: 8px;
    margin-right: 4px;
  }

  .header-span {
    line-height: 14px;
  }
}

.service-name {
  font-size: 20px;
}

.service-type {
  padding: 2px 4px;
}

.nav-link {
  color: rgba(255, 255, 255, 0.5);

  &.menu-label span {
    color: #fff;
    font-weight: 700;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.75);
  }

  :deep(.v-avatar) {
    height: 30px;
    width: 30px;

    .text-header {
      font-size: 12px;
      font-weight: 400;
    }
  }
}

:deep(.v-list) {
  hr {
    border-top: 1px solid #e9ecef;
  }
}

.header-dropdown-item {
  padding: 0 !important;

  &.v-list-item--density-compact.v-list-item--one-line {
    height: 30px;
    min-height: 30px;
  }

  :deep(.v-list-item__content) {
    width: 100%;
    height: 100%;

    .header-dropdown-link {
      padding: 0 24px;
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
}

.w-16 {
  width: 16px;
}

.box {
  width: 16px;
  height: 16px;
}
</style>
